import { useState } from "react";
import { Bell, BellRing, Check, CheckCheck, Inbox, Loader2, Monitor, MonitorOff } from "lucide-react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { useDesktopNotifications } from "@/hooks/useDesktopNotifications";
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type NotificationRow,
} from "@/hooks/useNotifications";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Topbar bell:
 *   - Shows a count badge of unread notifications (capped at 99+).
 *   - Click → dropdown with the latest 8 rows (unread highlighted).
 *   - Click a row → marks read + navigates to its link if it has one.
 *   - "Mark all" clears every unread in one call.
 *   - "View all" links to /notifications for the full paginated page.
 *
 * Live updates: useNotificationSocket (mounted once in AppLayout)
 * invalidates the queries when the WS pushes; this component just
 * subscribes via React Query.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const unread = useUnreadCount();
  const list = useNotifications();
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  const unreadCount = unread.data?.unread ?? 0;
  const items = (list.data?.results ?? []).slice(0, 8);

  function handleRowClick(n: NotificationRow) {
    if (!n.is_read) markRead.mutate(n.id);
    setOpen(false);
    // Navigation handled by the wrapping <Link>.
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          {unreadCount > 0 ? (
            <BellRing className="h-4 w-4" />
          ) : (
            <Bell className="h-4 w-4" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem] p-0">
        {/* ---- Header ---- */}
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <div>
            <div className="text-sm font-semibold">Notifications</div>
            {unreadCount > 0 ? (
              <div className="text-xs text-muted-foreground">
                {unreadCount} unread
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">All caught up</div>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost" size="sm"
              className="h-7 px-2 text-xs"
              disabled={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              {markAll.isPending
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <CheckCheck className="h-3.5 w-3.5" />}
              Mark all
            </Button>
          )}
        </div>

        {/* ---- Desktop-notification toggle ---- */}
        <DesktopNotifyRow />

        {/* ---- List ---- */}
        <div className="max-h-96 overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                <Inbox className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">No notifications yet</p>
              <p className="text-xs text-muted-foreground">
                We'll buzz here when something needs you.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {items.map((n) => (
                <li key={n.id}>
                  <NotificationItem n={n} onClick={() => handleRowClick(n)} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ---- Footer ---- */}
        <div className="border-t border-border/60 p-2">
          <Button asChild variant="ghost" size="sm" className="w-full justify-center">
            <Link to="/notifications" onClick={() => setOpen(false)}>
              View all notifications
            </Link>
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Single row — used both here and on the full /notifications page.
// ---------------------------------------------------------------------------

export function NotificationItem({
  n, onClick, dense = false,
}: {
  n: NotificationRow;
  onClick?: () => void;
  dense?: boolean;
}) {
  const Inner = (
    <div className={cn(
      "flex items-start gap-3 transition-colors",
      dense ? "px-3 py-3" : "px-4 py-3",
      "hover:bg-muted/40",
      !n.is_read && "bg-primary/5",
    )}>
      {/* Unread dot */}
      <div className="flex h-9 w-1 shrink-0 items-center">
        <span className={cn(
          "block h-2 w-2 rounded-full",
          n.is_read ? "bg-transparent" : "bg-primary",
        )} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="truncate text-sm font-medium">{n.title}</div>
          {n.is_read && (
            <Check className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/50" />
          )}
        </div>
        {n.message && (
          <p className="line-clamp-2 text-xs text-muted-foreground">{n.message}</p>
        )}
        <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground/70">
          {n.actor_name && <Badge variant="outline" className="font-normal">{n.actor_name}</Badge>}
          <span>{formatDateTime(n.created_at)}</span>
        </div>
      </div>
    </div>
  );

  // If the notification has a link, wrap the whole row in an anchor.
  // Otherwise it's a plain button (still clickable to mark-read).
  if (n.link) {
    return (
      <Link to={n.link} onClick={onClick} className="block">
        {Inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className="block w-full text-left">
      {Inner}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Desktop (OS-level) notification opt-in. Sits in the dropdown header so the
// user finds it next to the in-app notifications. Three visible states:
//   - browser unsupported    → don't render anything
//   - permission "denied"    → show a disabled hint (can't recover from JS)
//   - permission "default" or "granted"  → show a toggle pill
// ---------------------------------------------------------------------------

function DesktopNotifyRow() {
  const { supported, permission, enabled, toggle, sendTest } = useDesktopNotifications();
  const [busy, setBusy] = useState(false);

  if (!supported) return null;

  const active = enabled && permission === "granted";
  const denied = permission === "denied";

  async function onToggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (busy || denied) return;
    setBusy(true);
    try { await toggle(!active); } finally { setBusy(false); }
  }

  function onTest(e: React.MouseEvent) {
    e.stopPropagation();
    sendTest();
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 border-b border-border/60 px-4 py-2.5 text-xs",
        denied && "opacity-60",
      )}
      title={
        denied
          ? "Blocked by your browser. Enable notifications for this site in browser settings."
          : active
            ? "Desktop pop-ups are on for background tabs"
            : "Click the toggle to get OS-level pop-ups"
      }
    >
      <div className="flex items-center gap-2">
        {active ? (
          <Monitor className="h-3.5 w-3.5 text-primary" />
        ) : (
          <MonitorOff className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <span className="font-medium">
          Desktop notifications {denied ? "blocked" : active ? "on" : "off"}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {active && (
          <button
            type="button"
            onClick={onTest}
            className="rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            Test
          </button>
        )}
        <button
          type="button"
          onClick={onToggle}
          disabled={busy || denied}
          aria-pressed={active}
          aria-label={active ? "Turn desktop notifications off" : "Turn desktop notifications on"}
          className={cn(
            "inline-flex h-4 w-7 items-center rounded-full p-0.5 transition-colors",
            active ? "bg-primary" : "bg-muted",
            !busy && !denied && "cursor-pointer",
          )}
        >
          <span
            className={cn(
              "block h-3 w-3 rounded-full bg-card shadow-sm transition-transform",
              active ? "translate-x-3" : "translate-x-0",
            )}
          />
        </button>
      </div>
    </div>
  );
}
